import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AuthorCollectingSocietyCreateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CollectiveManagementSocietyCreate } from "../../../collectiveManagementSociety/components/create/CollectiveManagementSocietyCreate";
import { CollectiveManagementSocietyCreateStore } from "../../../collectiveManagementSociety/store";
import { InputString } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { TerritoryCreate } from "../../../territory/components/create/TerritoryCreate";
import { TerritoryCreateStore } from "../../../territory/store";
import { FormControl } from "@mui/material";

export type AuthorCollectingSocietyCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  collectingSocietySelectedValue?: { displayId: string; id: string } | null;
  onCollectingSocietySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onCollectingSocietyChange?: (value: string) => void;
  collectingSocietyOptions?: { displayId: string; id: string }[];
  collectingSocietyError?: string;
  memberNumberSelectedValue?: { displayId: string; id: string } | null;
  onMemberNumberChange?: (value: string) => void;
  memberNumber: string;
  memberNumberError?: string;
  territoriesSelectedValues: { displayId: string; id: string }[];
  onTerritoriesSelected?: (value: { displayId: string; id: string }[]) => void;
  onTerritoriesChange?: (value: string) => void;
  territoriesOptions?: { displayId: string; id: string }[];
  territoriesError?: string;
  authorCollectingSocietyCreateStore: AuthorCollectingSocietyCreateStore;
};

export const AuthorCollectingSocietyCreateForm: React.FC<AuthorCollectingSocietyCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onCollectingSocietySelected = () => {},
      collectingSocietySelectedValue = null,
      collectingSocietyOptions = [],
      collectingSocietyError,
      onCollectingSocietyChange = () => {},
      memberNumber = "",
      memberNumberError,
      onMemberNumberChange = () => {},
      onTerritoriesSelected = () => {},
      territoriesSelectedValues,
      territoriesOptions = [],
      territoriesError,
      onTerritoriesChange = () => {},
      authorCollectingSocietyCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AuthorCollectingSociety"
              mode="create"
              values={{}}
              options={{ collectingSociety: collectingSocietyOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "collectingSociety":
                    onCollectingSocietySelected?.(valor);
                    break;
                  case "memberNumber":
                    onMemberNumberChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={authorCollectingSocietyCreateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupAuthorCollectingSocietyLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="collectingSociety"
                  label={
                    langStore.AuthorCollectingSocietyCollectingSocietyLabel
                  }
                  autoFocus
                  error={Boolean(collectingSocietyError)}
                  helperText={
                    collectingSocietyError ||
                    authorCollectingSocietyCreateStore.warnings[
                      "collectingSociety"
                    ]?.message
                  }
                  selectedValue={collectingSocietySelectedValue}
                  onChange={(value: string) => onCollectingSocietyChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCollectingSocietySelected(value)
                  }
                  options={collectingSocietyOptions}
                  description={
                    langStore.AuthorCollectingSocietyCollectingSocietyDescription
                  }
                  canCreate={rootStore.authStore.hasPermissions(
                    "collectiveManagementSociety_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CollectiveManagementSocietyCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CollectiveManagementSocietyCreate
                          collectiveManagementSocietyCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCollectiveManagementSociety"
                      cancelMethod="cancelNewCollectiveManagementSociety"
                    />
                  )}
                />

                <InputString
                  value={memberNumber}
                  error={Boolean(memberNumberError)}
                  warning={
                    !memberNumberError &&
                    Boolean(
                      authorCollectingSocietyCreateStore.warnings[
                        "memberNumber"
                      ]
                    )
                  }
                  helperText={
                    memberNumberError ||
                    authorCollectingSocietyCreateStore.warnings["memberNumber"]
                      ?.message
                  }
                  label={langStore.AuthorCollectingSocietyMemberNumberLabel}
                  onChange={(value: string) => onMemberNumberChange(value)}
                  description={
                    langStore.AuthorCollectingSocietyMemberNumberDescription
                  }
                />
                <InputReferenceSet
                  id="territories"
                  label={langStore.AuthorCollectingSocietyTerritoriesLabel}
                  error={Boolean(territoriesError)}
                  helperText={
                    territoriesError ||
                    authorCollectingSocietyCreateStore.warnings["territories"]
                      ?.message
                  }
                  selectedValues={territoriesSelectedValues}
                  onChange={(value: string) => onTerritoriesChange(value)}
                  onSelect={(value: { displayId: string; id: string }[]) =>
                    onTerritoriesSelected(value)
                  }
                  options={territoriesOptions}
                  description={
                    langStore.AuthorCollectingSocietyTerritoriesDescription
                  }
                  pinnedDisplayIds={["Todo el mundo"]}
                  canCreate={rootStore.authStore.hasPermissions(
                    "territory_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new TerritoryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <TerritoryCreate
                          territoryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewTerritory"
                      cancelMethod="cancelNewTerritory"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
